import { VisionButton, VisionInput } from "@/components/VisionUI"
import { Field } from "@/components/ui/field"
import { Box, Flex, Heading, Text } from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { type SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import {
  type ApiError,
  type UserPublic,
  type UserUpdateMe,
  UsersService,
} from "@/client"
import useAuth from "@/hooks/useAuth"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import { emailPattern, handleError } from "@/utils"

const UserInformation = () => {
  const { t } = useTranslation(["settings", "common"])
  const sc = useSemanticColors()
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()
  const [editMode, setEditMode] = useState(false)
  const { user: currentUser } = useAuth()
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { isSubmitting, errors, isDirty },
  } = useForm<UserPublic>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      full_name: currentUser?.full_name,
      email: currentUser?.email,
    },
  })

  const toggleEditMode = () => {
    setEditMode(!editMode)
  }

  const mutation = useMutation({
    mutationFn: (data: UserUpdateMe) =>
      UsersService.updateUserMe({ requestBody: data }),
    onSuccess: () => {
      showSuccessToast(t("settings:profile.updated"))
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
    onSettled: () => {
      queryClient.invalidateQueries()
    },
  })

  const onSubmit: SubmitHandler<UserUpdateMe> = async (data) => {
    mutation.mutate(data)
  }

  const onCancel = () => {
    reset()
    toggleEditMode()
  }

  return (
    <>
      <Heading size="md" color={sc.heading} mb={4}>
        {t("settings:profile.title")}
      </Heading>
      <Box as="form" onSubmit={handleSubmit(onSubmit)}>
        <Field label={t("settings:profile.fullName")}>
          {editMode ? (
            <VisionInput
              {...register("full_name", { maxLength: 30 })}
              type="text"
              variant="glass"
            />
          ) : (
            <Text
              fontSize="md"
              py={2}
              color={!currentUser?.full_name ? "gray" : "inherit"}
            >
              {currentUser?.full_name || t("settings:profile.na")}
            </Text>
          )}
        </Field>
        <Field
          mt={4}
          label={t("settings:profile.email")}
          invalid={!!errors.email}
          errorText={errors.email?.message}
        >
          {editMode ? (
            <VisionInput
              {...register("email", {
                required: "Email is required",
                pattern: emailPattern,
              })}
              type="email"
              variant="glass"
            />
          ) : (
            <Text fontSize="md" py={2}>
              {currentUser?.email}
            </Text>
          )}
        </Field>
        <Flex mt={4} gap={3}>
          <VisionButton
            variant="brand"
            onClick={toggleEditMode}
            type={editMode ? "button" : "submit"}
            loading={editMode ? isSubmitting : false}
            disabled={editMode ? !isDirty || !getValues("email") : false}
          >
            {editMode ? t("common:actions.save") : t("common:actions.edit")}
          </VisionButton>
          {editMode && (
            <VisionButton
              variant="ghost"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t("common:actions.cancel")}
            </VisionButton>
          )}
        </Flex>
      </Box>
    </>
  )
}

export default UserInformation
